import { useState } from "react";
import { logger } from "@/services/LoggingService";
import { useToast } from "@/components/ui/use-toast";
import { getFundDisplayName } from "@/utils/fundDisplayUtils";
import { determineStandardAssetClass } from "@/utils/asset-class";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import type { TablesInsert } from "@/integrations/supabase/types";
import { fetchPortfoliosForUser } from "@/services/portfolio/PortfoliosService";
import { createPortfolio } from "@/services/portfolio/PortfolioService";
import { findAllocationIdWithType, insertAllocation } from "@/services/portfolio/PortfolioAllocationsService";
import { getDefaultCurrency } from "@/services/profile/UserProfileService";

// Define types used in the hook
export interface Portfolio {
  id: string;
  name: string;
  created_at: string;
  portfolio_type: string;
  updated_at: string;
  user_id: string;
  portfolio_size: number;
  risk_level: "low" | "medium" | "high";
  status: string;
  target_return: number;
}

interface UsePortfolioManagementProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export function usePortfolioManagement({ onSuccess, onClose }: UsePortfolioManagementProps) {
  // States for the dialog
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  // Initialize with empty string to show all portfolios without filtering
  const [portfolioType, setPortfolioType] = useState<string>("");
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Removed legacy per-allocation percentage selection state

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  // Reset form state
  const resetForm = () => {
    setIsCreatingNew(false);
    setNewPortfolioName("");
    setSelectedPortfolioId(null);
    setErrorMessage(null);
    setIsLoading(false);
  };

  // Helper to get selected portfolio name
  const getSelectedPortfolioName = () => {
    if (isCreatingNew) return newPortfolioName;
    const selectedPortfolio = (portfolios || []).find((p) => p.id === selectedPortfolioId);
    return selectedPortfolio?.name || "portfolio";
  };

  // Fetch existing portfolios
  const fetchPortfolios = async () => {
    try {
      // Gate on auth store; avoid network-dependent getUser() to reduce false negatives
      if (!userId) {
        logger.debug('usePortfolioManagement', 'Skip fetchPortfolios: no user in store');
        return;
      }
      const data = await fetchPortfoliosForUser(userId, portfolioType || undefined);

      const filteredData = (data || []).map((portfolio: any) => ({
        ...portfolio,
        portfolio_type: portfolio?.portfolio_type || 'personal',
      })) as Portfolio[];

      logger.info('usePortfolioManagement', 'Fetched portfolios', {
        count: filteredData?.length || 0,
        portfolioType,
        types: (filteredData || []).map(p => p.portfolio_type)
      });

      setPortfolios(filteredData);

      // Guard: do not override user's current selection on refetch.
      // Only set a default selection when there is no valid selection present
      // in the freshly fetched list.
      if (filteredData && (filteredData?.length || 0) > 0) {
        const hasValidSelection = !!selectedPortfolioId && (filteredData || []).some(p => p.id === selectedPortfolioId);
        if (!hasValidSelection) {
          setSelectedPortfolioId(filteredData[0].id);
        }
      }
    } catch (error) {
      console.error("Error in fetchPortfolios:", error);
    }
  };

  // Close handler
  const handleClose = () => {
    resetForm();
    if (onClose) {
      onClose();
    }
  };

  // Add to Portfolio functionality
  const addToPortfolio = async (fund: any) => {
    if (!fund) {
      console.error("No fund selected");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null); // Clear any previous error messages

    try {
      // Gate on auth store and avoid network-dependent getUser()
      if (!userId) {
        throw new Error("User not authenticated");
      }
      
      console.log("Current user:", userId);

      let portfolioId: string;
      let targetPortfolioType: string | undefined;

      // Create new portfolio if needed
      if (isCreatingNew) {
        if (!newPortfolioName?.trim()) {
          throw new Error("Portfolio name is required");
        }
        console.log("Creating new portfolio:", newPortfolioName);
        portfolioId = await createPortfolio({ userId, name: newPortfolioName.trim(), portfolioType: portfolioType || 'model' });
        targetPortfolioType = portfolioType || 'model';
        console.log("Created new portfolio with ID:", portfolioId);

        // Invalidate all portfolio-related queries to force refetch
        await queryClient.invalidateQueries({ queryKey: ['portfolio'] });
        await queryClient.invalidateQueries({ queryKey: ['portfolios'] }); // Also invalidate portfolio list queries
        
        // Refresh portfolios list
        await fetchPortfolios();
      } else {
        // Use existing portfolio
        if (!selectedPortfolioId) {
          throw new Error("No portfolio selected");
        }

        portfolioId = selectedPortfolioId;
        // Determine type from loaded portfolios list
        const selected = (portfolios || []).find((p) => p.id === selectedPortfolioId);
        targetPortfolioType = selected?.portfolio_type || 'personal';
      }

      // Determine normalized asset classification
      const assetType = determineStandardAssetClass(fund.sa_asset_class || fund.asset_class, undefined) || 'unclassified';
      console.log("Asset type determined for check:", assetType, "fund:", fund.id);
      
      // Check if this asset already exists in this portfolio with this asset type
      console.log("Checking for existing allocation...");
      const existingAllocId = await findAllocationIdWithType(portfolioId, fund.id, assetType);
      if (existingAllocId) {
        console.log("Fund already exists in portfolio");
        setErrorMessage(`${getFundDisplayName(fund)} is already in this portfolio`);
        setIsLoading(false);
        return;
      }

      console.log("Fund not found in portfolio, proceeding with addition");

      // Determine portfolio display/base currency from localStorage or user default
      let currencyCode: string = 'CAD';
      try {
        const lsKey = `portfolio:currency:${portfolioId}`;
        const fromLs = typeof window !== 'undefined' ? window.localStorage.getItem(lsKey) : null;
        if (fromLs && fromLs.trim()) {
          currencyCode = fromLs.trim().toUpperCase();
        } else {
          const dc = userId ? await getDefaultCurrency(userId) : null;
          if (dc) currencyCode = dc;
        }
      } catch {}

      // Create allocation record
      // Build insertion defaults based on portfolio type
      const insertPayload: TablesInsert<'portfolio_allocations'> = {
        portfolio_id: portfolioId,
        asset_id: fund.id,
        asset_type: assetType,
        allocation_percentage: 0,
        currency_code: currencyCode,
      };
      if ((targetPortfolioType || 'model') !== 'model') {
        insertPayload.allocation_percentage = 0; // keep 0% for readability
        insertPayload.allocation_value = 0;
        insertPayload.is_value_locked = true;
      }

      await insertAllocation(insertPayload as TablesInsert<'portfolio_allocations'>);

      // Get portfolio name using our helper
      const portfolioName = getSelectedPortfolioName();

      console.log("Successfully added to portfolio:", {
        portfolio_id: portfolioId,
        portfolio_name: portfolioName,
        asset_id: fund.id,
        asset_name: fund.fund_name,
        asset_type: "fund_raw",
      });

      // Invalidate portfolio queries to refresh data - be more specific
      await queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] });
      await queryClient.invalidateQueries({ queryKey: ['portfolio_allocations', portfolioId] });
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });

      // Show success toast notification
      toast({
        title: "Success!",
        description: `${getFundDisplayName(fund)} was added to ${portfolioName} successfully.`,
        variant: "default",
      });

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset the form state and close the dialog
      resetForm();

      // Close the dialog after a short delay to ensure the toast is visible
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error: any) {
      console.error("Error in handleAddToPortfolio:", error);
      setErrorMessage(error.message || "There was a problem adding this fund to your portfolio.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    isCreatingNew,
    newPortfolioName,
    portfolioType,
    selectedPortfolioId,
    portfolios,
    isLoading,
    errorMessage,

    // Functions
    setIsCreatingNew,
    setNewPortfolioName,
    setPortfolioType,
    setSelectedPortfolioId,
    resetForm,
    handleClose,
    fetchPortfolios,
    addToPortfolio,
    getSelectedPortfolioName
  };
}
